import { Controller } from "@hotwired/stimulus";

// Shared global counter for open dialogs (modals and slideovers)
// This ensures proper scrollbar compensation when mixing modals and slideovers
if (!window.__openDialogCount) {
  window.__openDialogCount = 0;
}

export default class extends Controller {
  static targets = ["dialog", "template"];
  static values = {
    lazyLoad: { default: false, type: Boolean }, // Whether to lazy load the modal content
    open: { default: false, type: Boolean }, // Whether the modal is open
    preventDismiss: { default: false, type: Boolean }, // Whether to prevent the modal from being dismissed
    turboFrameSrc: { default: "", type: String }, // URL for the turbo frame
  };

  backdropClose(event) {
    // Only close if clicking directly on the dialog element (the backdrop)
    if (event.target.nodeName === "DIALOG") {
      // Stop propagation to prevent closing parent dialogs
      event.stopPropagation();

      if (this.preventDismissValue) {
        this.bounce();
      } else {
        this.close();
      }
    }
  }

  beforeCache() {
    this.close();
  }

  // Add bounce animation to indicate modal won't close
  bounce() {
    // Prevent multiple bounces in quick succession
    if (this.isBouncing) return;

    this.isBouncing = true;

    // For a more pronounced effect, we can combine with scale
    this.dialogTarget.classList.add("scale-105", "transition-transform");

    setTimeout(() => {
      this.dialogTarget.classList.remove("scale-105");
      this.dialogTarget.classList.add("scale-100");

      setTimeout(() => {
        // Remove all animation classes
        this.dialogTarget.classList.remove("scale-100", "transition-transform");

        // Allow bouncing again after a short cooldown
        setTimeout(() => {
          this.isBouncing = false;
        }, 200); // Additional cooldown to prevent rapid bouncing
      }, 150);
    }, 150);
  }

  // Allows for a closing animation since display transitions don't work yet
  close() {
    // If not open, don't do anything
    if (!this.isOpen) return;

    this.dialogTarget.setAttribute("closing", "");

    Promise.all(this.dialogTarget.getAnimations().map((animation) => animation.finished)).then(() => {
      this.dialogTarget.removeAttribute("closing");
      this.dialogTarget.close();

      // Decrement the counter and mark this modal as closed
      window.__openDialogCount--;
      this.isOpen = false;

      // Only remove scrollbar compensation if no dialogs are open
      if (window.__openDialogCount === 0) {
        document.documentElement.style.removeProperty("--scrollbar-compensation");
        document.body.classList.remove("modal-open", "slideover-open");
      }

      // Reset bouncing flag
      this.isBouncing = false;
    });
  }

  connect() {
    if (this.openValue) this.open();
    this.boundBeforeCache = this.beforeCache.bind(this);
    document.addEventListener("turbo:before-cache", this.boundBeforeCache);
    this.contentLoaded = false;
    this.isBouncing = false;
    this.isOpen = false; // Track if this specific modal is open

    // Add event listener for when dialog is closed by any means (including Escape key)
    this.dialogTarget.addEventListener("close", this.handleDialogClose.bind(this));

    // Prevent Escape key from closing if preventDismiss is true
    this.dialogTarget.addEventListener("cancel", this.handleDialogCancel.bind(this));

    // Additional keydown listener for better escape key handling
    this.boundHandleKeydown = this.handleKeydown.bind(this);
    this.dialogTarget.addEventListener("keydown", this.boundHandleKeydown);

    // Close modal on successful turbo form submission
    this.boundHandleTurboSubmitEnd = this.handleTurboSubmitEnd.bind(this);
    this.dialogTarget.addEventListener("turbo:submit-end", this.boundHandleTurboSubmitEnd);
  }

  disconnect() {
    // If this modal was open when disconnecting, decrement the counter
    if (this.isOpen) {
      window.__openDialogCount--;
      this.isOpen = false;

      // Only remove scrollbar compensation if no dialogs are open
      if (window.__openDialogCount === 0) {
        document.documentElement.style.removeProperty("--scrollbar-compensation");
        document.body.classList.remove("modal-open", "slideover-open");
      }
    }

    document.removeEventListener("turbo:before-cache", this.boundBeforeCache);
    this.dialogTarget.removeEventListener("close", this.handleDialogClose.bind(this));
    this.dialogTarget.removeEventListener("cancel", this.handleDialogCancel.bind(this));
    this.dialogTarget.removeEventListener("keydown", this.boundHandleKeydown);
    this.dialogTarget.removeEventListener("turbo:submit-end", this.boundHandleTurboSubmitEnd);
  }

  // Calculate actual scrollbar width
  getScrollbarWidth() {
    // Create a temporary div to measure scrollbar width
    const outer = document.createElement("div");
    outer.style.visibility = "hidden";
    outer.style.overflow = "scroll";
    outer.style.msOverflowStyle = "scrollbar"; // Force scrollbars on IE/Edge
    document.body.appendChild(outer);

    const inner = document.createElement("div");
    outer.appendChild(inner);

    const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;
    outer.parentNode.removeChild(outer);

    return scrollbarWidth;
  }

  // Handle cancel event (Escape key)
  handleDialogCancel(event) {
    if (this.preventDismissValue) {
      event.preventDefault();
      event.stopPropagation();
      this.bounce();
      return false;
    }
  }

  // Handle cleanup when dialog is closed by any means
  handleDialogClose() {
    // If the modal was open, decrement the counter
    if (this.isOpen) {
      window.__openDialogCount--;
      this.isOpen = false;

      // Only remove scrollbar compensation if no dialogs are open
      if (window.__openDialogCount === 0) {
        document.documentElement.style.removeProperty("--scrollbar-compensation");
        document.body.classList.remove("modal-open", "slideover-open");
      }
    }

    // Ensure the closing attribute is removed
    this.dialogTarget.removeAttribute("closing");

    // Reset bouncing flag
    this.isBouncing = false;
  }

  // Handle keydown events
  handleKeydown(event) {
    if (event.key === "Escape" && this.preventDismissValue) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      this.bounce();
      return false;
    }
  }

  // Handle turbo:submit-end event - close modal on successful submission
  handleTurboSubmitEnd(event) {
    // Check if the submission was successful (no errors)
    if (event.detail.success) {
      this.close();
    }
  }

  hide() {
    this.close();
  }

  // Check if the device supports touch
  isTouchDevice() {
    return "ontouchstart" in window || navigator.maxTouchPoints > 0 || navigator.msMaxTouchPoints > 0;
  }

  async open() {
    // If already open, don't do anything
    if (this.isOpen) return;

    // If lazy loading is enabled and content hasn't been loaded yet, load it now
    if (this.lazyLoadValue && !this.contentLoaded) {
      await this.#loadTemplateContent();
      this.contentLoaded = true;
    }

    // Increment the counter and mark this modal as open
    window.__openDialogCount++;
    this.isOpen = true;

    // Only apply scrollbar compensation if this is the first dialog
    if (window.__openDialogCount === 1) {
      const scrollbarWidth = this.getScrollbarWidth();
      if (scrollbarWidth > 0) {
        // Set CSS custom property for scrollbar width
        document.documentElement.style.setProperty("--scrollbar-compensation", `${scrollbarWidth}px`);
        // Add class to body to trigger compensation
        document.body.classList.add("modal-open");
      }
    }

    this.dialogTarget.showModal();

    // On touch devices, remove initial focus to prevent awkward focus rings
    if (this.isTouchDevice()) {
      // Find the currently focused element within the dialog
      const focusedElement = this.dialogTarget.querySelector(":focus");
      if (focusedElement) {
        focusedElement.blur();
      }
    }
  }

  // For showing non-modally
  show() {
    this.dialogTarget.show();
  }

  async #loadTemplateContent() {
    // Find the container in the dialog to append content to
    const container = this.dialogTarget.querySelector("[data-modal-content]") || this.dialogTarget;

    // Check if we should use Turbo Frame lazy loading
    if (this.turboFrameSrcValue) {
      // Look for a turbo-frame in the container
      let turboFrame = container.querySelector("turbo-frame");

      if (!turboFrame) {
        // Create a turbo-frame if it doesn't exist
        turboFrame = document.createElement("turbo-frame");
        turboFrame.id = "modal-lazy-content";

        // Clear any loading indicators or placeholder content
        container.innerHTML = "";
        container.appendChild(turboFrame);
      }

      // Set the src to trigger the lazy load
      turboFrame.src = this.turboFrameSrcValue;

      // Wait for the turbo-frame to load
      return new Promise((resolve) => {
        const handleLoad = () => {
          turboFrame.removeEventListener("turbo:frame-load", handleLoad);
          resolve();
        };

        turboFrame.addEventListener("turbo:frame-load", handleLoad);

        // Fallback timeout in case the frame doesn't load
        setTimeout(() => {
          turboFrame.removeEventListener("turbo:frame-load", handleLoad);
          resolve();
        }, 5000);
      });
    } else if (this.hasTemplateTarget) {
      // Use template-based lazy loading (existing behavior)
      const templateContent = this.templateTarget.content.cloneNode(true);

      // Clear any loading indicators or placeholder content
      container.innerHTML = "";

      // Append the template content
      container.appendChild(templateContent);
    }
  }
}
